import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UTRModal from '../../Modal';
import MultiPlayerSearch from '../MultiPlayerSearch';
import ListPicker from '../ListPicker';
import PlayerChip from '../PlayerChip';
import SavedListModalActions from '../SavedListModalActions';
import useListPicker from '../ListPicker/useListPicker';
import { getPlayerId, getPlayerName } from '../playerShape';
import { handleSaveToListError } from '../savedListsSave';
import { requestOpenSavedList, savePlayersToSavedLists } from '../../../actions/saved-lists-actions';
import { showSuccessToast } from '../../../actions/notification-actions';
import { PopupTypes } from '../../../utils/constants';
import { buildAddPlayersToast } from './addPlayerToast';

import styles from './addPlayerToListModal.scss';

// selfrate emits lowercase data-testid; the ticket pins this exact prefix as the contract the web
// verifier's Playwright selectors drive against (`add-player-modal.*`).
const TESTID_PREFIX = 'add-player-modal';

const STEP_FIND = 1;
const STEP_ADD = 2;

const STEP1_DESKTOP_TITLE = 'Find Player';
const STEP1_MOBILE_TITLE = 'Step 1 of 2: Find Players';
// Single-step entry (from List Detail) replaces the two-step "Step 1 of 2: Find Players" chrome header
// with a plain sentence-case title; the shared shell strips Bootstrap's uppercase on the title-span.
const SINGLE_STEP_MOBILE_TITLE = 'Add Player to List';
const STEP1_INDICATOR = 'Step 1 of 2';
const STEP2_DESKTOP_TITLE = 'Add to List';
const STEP2_MOBILE_TITLE = 'Step 2 of 2: Add Players to List';
const STEP2_INDICATOR = 'Step 2 of 2';
const BACK_LABEL = 'Back';
const NEXT_LABEL = 'Next';
const SAVE_LABEL = 'Save';
const CANCEL_TESTID_SUFFIX = 'cancel';
const NEXT_TESTID_SUFFIX = 'next';
const SAVE_TESTID_SUFFIX = 'save';

const GENERIC_ERROR_TOAST_MESSAGE = 'We couldn\'t add these players to your lists. Please try again.';

const AddPlayerToListModal = ({ onCloseClick }) => {
    const dispatch = useDispatch();
    const onSuccess = useSelector((state) => state.popups[`${PopupTypes.ADD_PLAYER_TO_LIST_POPUP}OnSuccess`]);
    // Opened from List Detail (TMS-2177) the popup carries the current list's id here, so the modal
    // saves into that list; opened from the hub it's absent and the two-step picker chooses the list(s).
    const savedListIds = useSelector((state) => state.popups.savedListIds);
    // Set by List Detail's Add Player (TMS-2279): the target list is already known, so render one step
    // (search + Save straight into savedListIds) with no list-picker Step 2. Absent from the hub entry,
    // which keeps the two-step wizard.
    const singleStep = useSelector((state) => state.popups.singleStep);

    const [step, setStep] = useState(STEP_FIND);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // The wizard leaves the default "My Player List" unchecked (preCheckDefault: false) so a bulk
    // save never silently adds every selected player to it — the member opts in explicitly. When
    // launched pre-scoped to a list, that list is pre-checked via initialSavedListIds instead. The
    // single-step flow never renders the picker, so its lists fetch is skipped entirely.
    const picker = useListPicker({
        preCheckDefault: false,
        initialSavedListIds: savedListIds || [],
        enabled: !singleStep,
    });

    const hasSelection = selectedPlayers.length > 0;

    // Removing the last chip on Step 2 would strand the member on a step with nothing to save; drop
    // back to search so the wizard can never reach a dead-end where Save is a silent no-op.
    useEffect(() => {
        if (step === STEP_ADD && !hasSelection) {
            setStep(STEP_FIND);
        }
    }, [step, hasSelection]);

    const handleAddPlayer = (player) => {
        const playerId = getPlayerId(player);
        setSelectedPlayers((prev) => (
            prev.some((existing) => getPlayerId(existing) === playerId) ? prev : [...prev, player]
        ));
    };

    const handleRemovePlayer = (playerId) => {
        setSelectedPlayers((prev) => prev.filter((player) => getPlayerId(player) !== playerId));
    };

    const goToAddStep = () => {
        if (hasSelection) {
            setStep(STEP_ADD);
        }
    };

    const goToFindStep = () => setStep(STEP_FIND);

    // The success toast's "View List" action. Single-step is launched from List Detail, so the member
    // is already viewing the target list — just dismiss the toast. The hub two-step jumps to the list,
    // but only when the single target is an existing list we have an id for (a brand-new list's id
    // isn't returned by the save), otherwise it also just dismisses.
    const buildViewListAction = (listIds, newListName) => {
        if (singleStep) {
            return (closeToast) => closeToast();
        }
        const targetListId = (!newListName && listIds.length === 1) ? listIds[0] : null;
        return (closeToast) => {
            if (targetListId !== null && targetListId !== undefined) {
                dispatch(requestOpenSavedList(targetListId));
            }
            closeToast();
        };
    };

    // Shared save orchestration for both entries: commit the selected players to the resolved lists,
    // then close + toast + refresh. onError localizes the two entries' differing error handling (the
    // two-step wizard can surface a duplicate-new-list-name inline; single-step has no name field).
    const commitSelectedPlayers = async ({ listIds, newListName, onError }) => {
        setIsSaving(true);
        const playerIds = selectedPlayers.map(getPlayerId);
        // The try only wraps the save call itself — post-success work (closing, toasting, the
        // onSuccess re-fetch) must never be reported as a failed save, which would invite a
        // duplicate re-submit of players that were actually added.
        try {
            await dispatch(savePlayersToSavedLists({ // NOSONAR — Redux thunk returns a promise
                playerIds,
                listIds,
                newListName,
            }));
        } catch (error) {
            onError(error);
            setIsSaving(false);
            return;
        }

        onCloseClick();
        dispatch(showSuccessToast(buildAddPlayersToast({
            playerCount: playerIds.length,
            playerName: getPlayerName(selectedPlayers[0]),
            listCount: listIds.length + (newListName ? 1 : 0),
            onViewList: buildViewListAction(listIds, newListName),
        })));
        if (onSuccess) {
            onSuccess();
        }
    };

    const handleSave = async () => {
        if (!picker.isPickerFilled || isSaving || !hasSelection) {
            return;
        }
        picker.setNameError(null);
        const { trimmedNewListName } = picker;
        await commitSelectedPlayers({
            listIds: picker.getSaveListIds(),
            newListName: trimmedNewListName || null,
            onError: (error) => handleSaveToListError({
                error,
                hasNewListName: trimmedNewListName.length > 0,
                dispatch,
                setNameError: picker.setNameError,
                genericMessage: GENERIC_ERROR_TOAST_MESSAGE,
            }),
        });
    };

    const handleSingleStepSave = async () => {
        // Bail if the target list is missing rather than POSTing listIds:undefined — a save that then
        // "succeeds" would throw at listIds.length after the players already landed, stranding the modal
        // open and inviting a re-submit. List Detail always supplies it; this guards a future caller
        // that sets singleStep without a scoped list.
        if (isSaving || !hasSelection || !savedListIds?.length) {
            return;
        }
        await commitSelectedPlayers({
            listIds: savedListIds,
            newListName: null,
            // No new-list name in this flow, so any failure is always the generic error toast — never
            // an inline name error (handleSaveToListError falls through to the toast when hasNewListName
            // is false).
            onError: (error) => handleSaveToListError({
                error,
                hasNewListName: false,
                dispatch,
                setNameError: () => {},
                genericMessage: GENERIC_ERROR_TOAST_MESSAGE,
            }),
        });
    };

    const renderChips = () => (
        <div className={styles.chipsStrip} data-testid={`${TESTID_PREFIX}.chips`}>
            {selectedPlayers.map((player) => {
                const playerId = getPlayerId(player);
                return (
                    <PlayerChip
                        key={playerId}
                        data-testid={`${TESTID_PREFIX}.chip-${playerId}`}
                        name={getPlayerName(player)}
                        onRemove={() => handleRemovePlayer(playerId)}
                    />
                );
            })}
        </div>
    );

    // Step 1 for both entries. Single-step (from List Detail) drops the "Step 1 of 2" indicator and
    // saves straight into the current list ("Save"); the hub's two-step wizard keeps the indicator and
    // advances to the list picker ("Next"). The body (search field) and the pinned-footer/scroll layout
    // are shared, so both stay in lockstep. `.single-step` is the positive mode handle the ticket pins.
    const renderSearchStep = () => (
        <>
            <div
                className={styles.stepHeader}
                data-testid={singleStep && `${TESTID_PREFIX}.single-step`}
            >
                <span
                    className={singleStep ? styles.singleStepTitle : `${styles.stepTitle} d-none d-md-block`}
                    data-testid={`${TESTID_PREFIX}.step1-title`}
                >
                    {STEP1_DESKTOP_TITLE}
                </span>
                {!singleStep && (
                    <span
                        className={`${styles.stepIndicator} d-none d-md-block`}
                        data-testid={`${TESTID_PREFIX}.step-indicator`}
                    >
                        {STEP1_INDICATOR}
                    </span>
                )}
            </div>
            <MultiPlayerSearch
                selectedPlayers={selectedPlayers}
                onAdd={handleAddPlayer}
                onRemove={handleRemovePlayer}
            />
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCloseClick}
                onSave={singleStep ? handleSingleStepSave : goToAddStep}
                saveDisabled={singleStep ? (!hasSelection || isSaving) : !hasSelection}
                saveLabel={singleStep ? SAVE_LABEL : NEXT_LABEL}
                cancelTestidSuffix={CANCEL_TESTID_SUFFIX}
                primaryTestidSuffix={singleStep ? SAVE_TESTID_SUFFIX : NEXT_TESTID_SUFFIX}
                centered
                pinned
            />
        </>
    );

    const renderAddStep = () => (
        <>
            <div className={styles.stepHeader}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={goToFindStep}
                    data-testid={`${TESTID_PREFIX}.back`}
                >
                    <i className="fas fa-chevron-left" />
                    <span data-testid={`${TESTID_PREFIX}.back.label`}>{BACK_LABEL}</span>
                </button>
                <span
                    className={`${styles.stepTitle} d-none d-md-block`}
                    data-testid={`${TESTID_PREFIX}.step2-title`}
                >
                    {STEP2_DESKTOP_TITLE}
                </span>
                <span
                    className={`${styles.stepIndicator} d-none d-md-block`}
                    data-testid={`${TESTID_PREFIX}.step-indicator`}
                >
                    {STEP2_INDICATOR}
                </span>
            </div>
            {renderChips()}
            <ListPicker data-testid={TESTID_PREFIX} {...picker.listPickerProps} />
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCloseClick}
                onSave={handleSave}
                saveDisabled={!picker.isPickerFilled || isSaving || !hasSelection}
                cancelTestidSuffix={CANCEL_TESTID_SUFFIX}
                primaryTestidSuffix={SAVE_TESTID_SUFFIX}
                centered
                pinned
            />
        </>
    );

    const mobileTitle = () => {
        if (singleStep) {
            return SINGLE_STEP_MOBILE_TITLE;
        }
        return step === STEP_FIND ? STEP1_MOBILE_TITLE : STEP2_MOBILE_TITLE;
    };

    // Single-step never leaves STEP_FIND (its footer saves rather than advancing), so the search step
    // covers both the single-step entry and the hub wizard's Step 1; only the hub reaches STEP_ADD.
    const renderBody = () => (step === STEP_ADD ? renderAddStep() : renderSearchStep());

    return (
        <UTRModal
            title={mobileTitle()}
            onCloseClick={onCloseClick}
            className={styles.addPlayerModal}
            data-testid={TESTID_PREFIX}
        >
            {renderBody()}
        </UTRModal>
    );
};

AddPlayerToListModal.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
};

export default AddPlayerToListModal;
